import { useState } from "react";
import { ListGroup, ListGroupItem } from "react-bootstrap";
import { ArrowDownCircleFill, ArrowRightCircleFill } from 'react-bootstrap-icons';

import "./NestedList.css";

/**
 * NestedList component
 *
 * Renders a collapsible, recursive list based on hierarchical data.
 * Each item can have nested child items, which are shown or hidden on click.
 *
 * @component
 * @param {Object[]} data - Array of list items to display.
 * @param {number|string} data[].key - Unique identifier for each item.
 * @param {string} data[].label - Display label for the item.
 * @param {Object[]} data[].nested - Optional nested child items (same shape).
 *
 * @example
 * const data = [
 *   {
 *     key: 1,
 *     label: "Parent 1",
 *     nested: [
 *       {
 *         key: 101,
 *         label: "Child 1.1",
 *         nested: [],
 *       },
 *     ],
 *   },
 *   {
 *     key: 2,
 *     label: "Parent 2",
 *     nested: [],
 *   },
 * ];
 *
 * <NestedList data={data} />
 */
const NestedList = ({ data }) => {
    const [expandedKeys, setExpandedKeys] = useState([]);

    const toggleExpand = (key) => {
        setExpandedKeys((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };

    const canExpand = (hasChildren, level) => {
        return hasChildren && level !== 1 && level !== 2 && level !== 3  && level !== 4;
    };

    const renderItems = (items, level = 0) => {
        return items.map((item) => {
            const hasChildren = item.nested?.length > 0;
            const isExpanded = expandedKeys.includes(item.key);

            return (
                <ListGroupItem
                    key={item.key}
                    className={`nested-list__item level-${level} border-none`}
                >
                    <div
                        onClick={() => hasChildren && toggleExpand(item.key)}
                        className="nested-list__content"
                        style={{ cursor: canExpand(hasChildren, level) ? "pointer" : "default" }}
                    >
                        {canExpand(hasChildren, level) && (
                            <span className="nested-list__expand-icon" aria-hidden="true">
                                {isExpanded ? <ArrowDownCircleFill /> : <ArrowRightCircleFill />}
                            </span>
                        )}

                        <span className={`nested-list__label nested-list__item-level-${level}`}>{item.label}</span>
                    </div>

                    {hasChildren && (level === 0 ? isExpanded : true) && (
                        <ListGroup variant="flush" className="nested-list">
                            {renderItems(item.nested, level + 1)}
                        </ListGroup>
                    )}
                </ListGroupItem>
            );
        });
    };

    return (
        <ListGroup variant="flush" className="nested-list">
            {renderItems(data)}
        </ListGroup>
    );
};

export default NestedList;
